// Procedural solid-surface planets for generated star systems. One
// program covers every rocky archetype; the archetype is a uniform, so
// every planet in a system shares the compiled shader and only differs
// by its uniform block (seed, palette, sea level, crater density...).
//
// Archetypes (uArchetype):
//   0 cratered  airless Mercury/Moon: highlands, dark maria, crater
//               fields at three scales, bright young ejecta
//   1 desert    Mars-like: warped highlands, canyon systems, lowland
//               dunes, dark albedo provinces, polar caps
//   2 ice       Europa-like: fractured sheets, reddish lineae, blue
//               crevasses, chaos terrain, glossy
//   3 ocean     near-global ocean, scattered islands, heavy cloud
//   4 terran    continents (warped FBM + ridged ranges), biomes by
//               latitude, altitude and moisture, specular ocean glint,
//               ice caps, animated clouds with ground shadow
//   5 lava      dark crust split by glowing cracks and lava lakes; the
//               glow is emissive so the night side burns too
//   6 toxic     Venus-like: thick banded sulphur haze, no visible ground
//
// Noise is sampled on the object-space normal (no seams, no pole
// pinch) offset by a per-planet seed. Relief comes from screen-space
// derivative bump mapping (Mikkelsen 2010), so there is no second
// height lookup, and octave count falls with the on-screen footprint so
// distant planets don't sparkle.
//
// Lighting is the host star's colour (uSunColor, from its blackbody
// temperature) along uSunDir, which builder.js refreshes each frame in
// world space. Colour-to-colour gradients go through OKLCh.

import { OKLAB_MIX_GLSL } from './oklab.js';
import { NOISE3D_GLSL } from './noise3d.js';

export const PLANET_ARCHETYPES = {
  cratered: 0, desert: 1, ice: 2, ocean: 3, terran: 4, lava: 5, toxic: 6
};

export const proceduralPlanetVertexShader = `
  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalSunDir;
  uniform vec3 uSunDir;

  void main() {
    vLocalNormal = normalize(normal);
    mat3 m = mat3(modelMatrix);
    vWorldNormal = normalize(m * normal);
    // Rotation (+ uniform scale) only, so the transpose is the inverse
    // up to scale: sun direction in the mesh's own spinning frame, for
    // the cloud-shadow offset.
    vLocalSunDir = normalize(transpose(m) * uSunDir);
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    // Camera-relative (see proceduralGasGiant.js): world space on the GPU
    // cracks the mesh far from the origin in realistic scale.
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const proceduralPlanetFragmentShader = `
  precision highp float;

  varying vec3 vLocalNormal;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPos;
  varying vec3 vLocalSunDir;

  uniform float uTime;
  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform float uSunIntensity;
  uniform float uArchetype;
  uniform vec3 uSeed;
  uniform float uRadius;
  uniform vec3 uC0;
  uniform vec3 uC1;
  uniform vec3 uC2;
  uniform vec3 uC3;
  uniform vec3 uC4;
  uniform vec3 uC5;
  uniform float uSeaLevel;
  uniform float uCraterDensity;
  uniform float uIceCap;
  uniform float uCloudCover;
  uniform vec3 uCloudColor;
  uniform float uBump;
  uniform float uFeature;
  uniform float uGlow;
  uniform vec3 uAtmoColor;
  uniform float uAtmoDensity;

  ${OKLAB_MIX_GLSL}
  ${NOISE3D_GLSL}

  float gOct;

  // Crater field: vec2(height, youth). Bowl below the datum, raised rim,
  // soft ejecta apron; one in seven craters is young and bright.
  vec2 craters(vec3 p, float scale, float density) {
    vec3 q = p * scale;
    vec3 i = floor(q);
    vec3 f = fract(q);
    float h = 0.0;
    float youth = 0.0;
    for (int z = -1; z <= 1; z++)
    for (int y = -1; y <= 1; y++)
    for (int x = -1; x <= 1; x++) {
      vec3 o = vec3(float(x), float(y), float(z));
      vec3 cell = i + o;
      if (hash31(cell + 17.3) > density) continue;
      vec3 c = o + hash33(cell) - f;
      float rad = mix(0.16, 0.5, pow(hash31(cell + 5.1), 2.0));
      float d = length(c) / rad;
      if (d > 2.2) continue;
      float bowl = d < 1.0 ? (d * d - 1.0) : 0.0;
      float rim = exp(-pow((d - 1.0) * 3.6, 2.0)) * 0.42;
      float apron = d > 1.0 ? exp(-(d - 1.0) * 2.4) * 0.08 : 0.0;
      h += (bowl * 0.9 + rim + apron) * rad;
      float young = step(0.86, hash31(cell + 9.7));
      youth = max(youth, young * smoothstep(2.2, 0.8, d));
    }
    return vec2(h, youth);
  }

  vec3 warp(vec3 p, float amt, float freq) {
    return p + amt * vec3(
      fbm(p * freq + vec3(1.7, 0.0, 3.3), 3.0),
      fbm(p * freq + vec3(8.1, 2.9, 0.4), 3.0),
      fbm(p * freq + vec3(4.4, 6.6, 9.2), 3.0));
  }

  // Screen-space derivative bump (Mikkelsen, "Bump Mapping Unparametrized
  // Surfaces on the GPU").
  vec3 perturbNormal(vec3 N, vec3 pos, float h) {
    vec3 dpdx = dFdx(pos);
    vec3 dpdy = dFdy(pos);
    float dhdx = dFdx(h);
    float dhdy = dFdy(h);
    vec3 r1 = cross(dpdy, N);
    vec3 r2 = cross(N, dpdx);
    float det = dot(dpdx, r1);
    vec3 grad = sign(det) * (dhdx * r1 + dhdy * r2);
    return normalize(abs(det) * N - grad);
  }

  void main() {
    vec3 n = normalize(vLocalNormal);
    vec3 p = n + uSeed;
    float lat = abs(n.y);
    float arch = uArchetype;

    // Octaves from the pixel footprint on the unit sphere.
    float footprint = max(length(fwidth(n)), 1e-5);
    gOct = clamp(log2(1.0 / (footprint * 2.0)) - 0.5, 1.5, 7.0);

    vec3 albedo = uC0;
    float height = 0.0;
    float water = 0.0;
    float gloss = 0.0;
    vec3 emit = vec3(0.0);
    float cloud = 0.0;
    float cloudShadow = 0.0;
    float wrapLight = uAtmoDensity * 0.12;

    if (arch < 0.5) {
      // ---- cratered ----
      float base = fbm(p * 2.2, gOct);
      float mare = smoothstep(0.08, 0.34, fbm(p * 1.05 + 3.0, 3.0));
      vec2 c1 = craters(p, 5.0, uCraterDensity);
      vec2 c2 = craters(p, 15.0, uCraterDensity);
      vec2 c3 = gOct > 4.0 ? craters(p, 44.0, uCraterDensity * 0.9) : vec2(0.0);
      // Maria are younger floods: fewer craters show through.
      float crat = c1.x + (c2.x * 0.6 + c3.x * 0.35) * (1.0 - mare * 0.7);
      height = base * 0.18 + crat - mare * 0.12;
      albedo = oklchMix(uC0, uC1, mare * 0.9);
      albedo *= 0.86 + 0.24 * fbm(p * 11.0, min(gOct, 4.0));
      float youth = max(c1.y, c2.y * 0.8);
      albedo = oklchMix(albedo, uC2, youth * 0.55);
    } else if (arch < 1.5) {
      // ---- desert ----
      vec3 w = warp(p, 0.35, 1.4);
      float cont = fbm(w * 1.7, gOct);
      float canyon = pow(ridged(w * 2.3 + 5.0, min(gOct, 4.0)), 5.0) * smoothstep(-0.25, 0.25, cont);
      float low = smoothstep(0.05, -0.35, cont);
      float dunes = sin(dot(w, vec3(38.0, 11.0, 27.0)) * uFeature + fbm(w * 5.0, 2.0) * 7.0);
      vec2 c1 = craters(p, 6.0, uCraterDensity);
      height = cont * 0.55 - canyon * 0.7 + dunes * 0.025 * low + c1.x * 0.5;
      float dark = smoothstep(0.1, 0.45, fbm(p * 1.9 + 21.0, 4.0));
      albedo = oklchMix(uC0, uC1, smoothstep(-0.2, 0.5, cont));
      albedo = oklchMix(albedo, uC3, dark * 0.65);
      albedo = oklchMix(albedo, uC2, canyon * 0.8);
      albedo *= 0.9 + 0.18 * fbm(p * 14.0, min(gOct, 4.0));
      float capEdge = 1.0 - uIceCap + fbm(p * 5.0, 3.0) * 0.06;
      float cap = smoothstep(capEdge - 0.015, capEdge + 0.015, lat);
      albedo = mix(albedo, uC4, cap);
      gloss = cap * 0.15;
    } else if (arch < 2.5) {
      // ---- ice ----
      float base = fbm(p * 2.0, gOct);
      albedo = oklchMix(uC0, uC1, smoothstep(-0.25, 0.65, fbm(p * 1.2 + 2.0, 4.0)));
      vec3 w = warp(p, 0.25, 1.1);
      float l1 = 1.0 - abs(snoise(w * vec3(2.6, 3.4, 2.2)));
      float l2 = 1.0 - abs(snoise(w * vec3(4.8, 3.9, 5.3) + 7.0));
      float lineae = max(smoothstep(0.965, 0.995, l1), smoothstep(0.975, 0.997, l2) * 0.8);
      float lineHalo = max(smoothstep(0.88, 0.99, l1), smoothstep(0.9, 0.99, l2)) * 0.5;
      // Crevasses: a sparse cellular network, faded out once the cells
      // shrink toward a pixel so it never turns into a mesh of dots.
      vec2 wc = worley(p * 3.2 * uFeature + base * 0.5);
      float crackW = 0.035 + footprint * 3.2 * uFeature * 2.0;
      float crack = (1.0 - smoothstep(0.0, crackW, wc.y - wc.x)) * smoothstep(0.02, 0.006, footprint * uFeature);
      crack *= smoothstep(-0.2, 0.3, fbm(p * 2.3 + 17.0, 3.0));
      float chaos = smoothstep(0.35, 0.55, fbm(p * 1.6 + 31.0, 3.0));
      vec2 wc2 = gOct > 4.5 ? worley(p * 18.0) : vec2(0.5, 1.0);
      float blocks = chaos * (1.0 - smoothstep(0.0, 0.12, wc2.y - wc2.x)) * smoothstep(4.5, 5.5, gOct);
      height = base * 0.2 + lineae * 0.25 - crack * 0.35 - blocks * 0.25 + wc.x * 0.12;
      albedo = oklchMix(albedo, uC2, max(lineHalo, lineae));
      albedo = oklchMix(albedo, uC3, crack * 0.6 + blocks * 0.35);
      albedo = oklchMix(albedo, uC2 * 0.8, chaos * 0.35);
      gloss = 0.45 * (1.0 - chaos * 0.6);
    } else if (arch < 4.5) {
      // ---- ocean / terran ----
      vec3 w = warp(p, 0.22, 1.1);
      float cont = fbm(w * 1.15, gOct);
      float ranges = ridged(w * 3.2 + 2.0, min(gOct, 5.0));
      float e = cont + ranges * 0.32 * smoothstep(-0.05, 0.25, cont) - uSeaLevel;
      if (e < 0.0) {
        float depth = -e;
        albedo = oklchMix(uC1, uC0, smoothstep(0.0, 0.22, depth));
        // Shelf turquoise right at the coast.
        albedo = oklchMix(albedo, uC1 * 1.35, smoothstep(0.03, 0.0, depth) * 0.6);
        water = 1.0;
        height = 0.0;
      } else {
        float moist = fbm(p * 2.4 + 11.0, 4.0) - e * 0.8;
        float temp = 1.0 - lat * 1.15 - e * 1.4 + fbm(p * 3.1 + 5.0, 3.0) * 0.15;
        vec3 veg = oklchMix(uC2, uC2 * 0.6, smoothstep(0.0, 0.5, moist));
        vec3 land = oklchMix(uC3, veg, smoothstep(-0.25, 0.15, moist));
        land = oklchMix(land, uC4, smoothstep(0.18, 0.42, e));
        land = oklchMix(land, uC5, smoothstep(0.2, 0.05, temp));
        land *= 0.88 + 0.2 * fbm(p * 16.0, min(gOct, 4.0));
        // Beaches.
        land = oklchMix(uC3 * 1.15, land, smoothstep(0.0, 0.025, e));
        albedo = land;
        height = e * 1.4;
      }
      float capEdge = 1.0 - uIceCap + fbm(p * 4.0, 3.0) * 0.05;
      float cap = smoothstep(capEdge - 0.02, capEdge + 0.02, lat);
      albedo = mix(albedo, uC5, cap);
      water *= 1.0 - cap;

      // Clouds: slow zonal drift plus a time-warped field so they churn.
      float ang = uTime * 0.012;
      mat3 rotY = mat3(cos(ang), 0.0, -sin(ang), 0.0, 1.0, 0.0, sin(ang), 0.0, cos(ang));
      vec3 cp = rotY * n + uSeed * 1.37;
      vec3 cw = cp + 0.28 * vec3(fbm(cp * 2.2 + uTime * 0.006, 3.0), 0.0, fbm(cp * 2.2 + 9.0 - uTime * 0.006, 3.0));
      cw.y *= 1.6; // stretch into zonal streaks
      // Weather systems (coarse cover mask) carrying fine cumulus detail;
      // the narrow smoothstep gives crisp cloud edges and clear gaps.
      float systems = fbm(cw * 1.6, 3.0) * 0.5 + 0.5 + 0.1 * cos(n.y * 9.0);
      float detail = fbm(cw * 7.0 + 3.0, max(gOct - 1.0, 1.0)) * 0.5 + 0.5;
      float cl = systems * 0.62 + detail * 0.38;
      float thr = 1.0 - uCloudCover;
      cloud = smoothstep(thr, thr + 0.08, cl) * (0.55 + 0.45 * smoothstep(thr, thr + 0.2, cl));
      vec3 sp = cw + normalize(rotY * vLocalSunDir) * 0.02;
      float cls = (fbm(sp * 1.6, 3.0) * 0.5 + 0.5 + 0.1 * cos(n.y * 9.0)) * 0.62 + detail * 0.38;
      cloudShadow = smoothstep(thr, thr + 0.08, cls);
      gloss = 0.0;
    } else if (arch < 5.5) {
      // ---- lava ----
      float base = fbm(p * 2.4, gOct);
      vec3 w = p * 5.5 * uFeature + 0.35 * vec3(fbm(p * 3.5, 3.0));
      vec2 wc = worley(w);
      float edge = wc.y - wc.x;
      float crack = 1.0 - smoothstep(0.0, 0.07, edge);
      vec2 wf = gOct > 3.5 ? worley(w * 3.3 + 4.0) : vec2(0.5, 1.0);
      float fine = (1.0 - smoothstep(0.0, 0.05, wf.y - wf.x)) * 0.55;
      float lake = smoothstep(0.12, 0.34, -base);
      float molten = clamp(max(max(crack, fine), lake), 0.0, 1.0);
      float pulse = 0.78 + 0.22 * sin(uTime * 0.7 + base * 14.0 + wc.x * 6.0);
      float heat = molten * pulse;
      vec3 crust = oklchMix(uC0, uC1, fbm(p * 7.0, min(gOct, 4.0)) * 0.5 + 0.5);
      albedo = mix(crust, uC2 * 0.25, molten);
      height = base * 0.45 - molten * 0.25 + wc.x * 0.2;
      vec3 lavaCol = oklchMix(uC2, uC3, smoothstep(0.3, 1.0, heat));
      emit = lavaCol * heat * heat * uGlow;
      // Faint smoky glow through the crust near hot zones.
      emit += uC2 * 0.04 * smoothstep(0.5, 0.0, edge) * uGlow;
    } else {
      // ---- toxic ----
      vec3 w = p + 0.3 * vec3(fbm(p * 1.8 + uTime * 0.004, 4.0), 0.0, fbm(p * 1.8 + 5.0, 4.0));
      float y = n.y + 0.08 * fbm(w * 3.0, 4.0);
      // Chevron: bands bend toward the equator like Venus' Y feature.
      float chev = y + 0.12 * abs(fbm(vec3(w.x, 0.0, w.z) * 1.5, 3.0));
      float band = sin(chev * uFeature * 3.14159 + fbm(w * 2.6, gOct) * 2.2) * 0.5 + 0.5;
      albedo = oklchMix(uC0, uC1, band);
      float streak = smoothstep(0.15, 0.55, fbm(w * vec3(4.0, 12.0, 4.0) + 3.0, min(gOct, 5.0)));
      albedo = oklchMix(albedo, uC2, streak * 0.45);
      albedo *= 0.92 + 0.12 * fbm(w * 9.0, min(gOct, 4.0));
      wrapLight = 0.35;
    }

    // ---- lighting ----
    vec3 N0 = normalize(vWorldNormal);
    // Derivatives outside any per-pixel branch, then select.
    vec3 Nb = perturbNormal(N0, vWorldPos, height * uBump * uRadius * 0.02);
    vec3 N = (uBump > 0.0 && water < 0.5) ? Nb : N0;
    vec3 L = normalize(uSunDir);
    vec3 V = normalize(cameraPosition - vWorldPos);
    float sunI = uSunIntensity > 0.001 ? uSunIntensity : 1.0;
    vec3 light = uSunColor * sunI;

    float ndl0 = dot(N0, L);
    float ndl = dot(N, L);
    float diff = clamp((ndl + wrapLight) / (1.0 + wrapLight), 0.0, 1.0);
    // Terminator from the smooth normal so bumps can't light the night.
    diff *= smoothstep(-0.1 - wrapLight * 0.3, 0.06, ndl0);

    vec3 col = albedo * light * diff * 1.15;
    col += albedo * 0.006; // faint starlight on the night side

    // Ground shadow from clouds, then specular on open water / ice.
    col *= 1.0 - cloudShadow * 0.45 * (1.0 - cloud);
    float nv = max(dot(N0, V), 0.0);
    float lit = smoothstep(-0.02, 0.12, ndl0);
    if (water > 0.0 || gloss > 0.0) {
      vec3 H = normalize(L + V);
      float fres = 0.02 + 0.98 * pow(1.0 - nv, 5.0);
      float spec = water * (pow(max(dot(N0, H), 0.0), 300.0) * 1.6 + pow(max(dot(N0, H), 0.0), 28.0) * 0.12);
      spec += gloss * pow(max(dot(N, H), 0.0), 60.0) * 0.8;
      col += light * spec * lit * (1.0 - cloud);
      col += light * water * fres * 0.12 * lit * (1.0 - cloud);
    }

    if (cloud > 0.0) {
      float cdiff = clamp((ndl0 + 0.12) / 1.12, 0.0, 1.0);
      vec3 cloudLit = uCloudColor * light * cdiff * 1.2 + uCloudColor * 0.004;
      col = mix(col, cloudLit, cloud);
    }

    // Limb haze on the disc for bodies with air; the rim shell draws
    // the part outside the silhouette.
    if (uAtmoDensity > 0.0) {
      float limb = pow(1.0 - nv, 2.6);
      float hazeLit = smoothstep(-0.25, 0.45, ndl0);
      col = mix(col, uAtmoColor * light * hazeLit, clamp(limb * uAtmoDensity * 0.8, 0.0, 0.85));
    }

    col += emit;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Rim shell: a slightly larger back-faced sphere, additive. Glow is the
// ray's closest approach through the shell (optical depth proxy), lit by
// where that point sits relative to the star, with a forward-scatter
// lobe when the planet is backlit.
export const atmosphereShellVertexShader = `
  varying vec3 vWorldPos;
  varying vec3 vCenter;
  varying float vScale;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vCenter = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
    vScale = length(modelMatrix[0].xyz);
    // Camera-relative (see proceduralGasGiant.js): world space on the GPU
    // cracks the mesh far from the origin in realistic scale.
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const atmosphereShellFragmentShader = `
  precision highp float;
  varying vec3 vWorldPos;
  varying vec3 vCenter;
  varying float vScale;
  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform vec3 uAtmoColor;
  uniform float uPlanetRadius;
  uniform float uShellRadius;
  uniform float uStrength;
  uniform float uFalloff;

  void main() {
    float R = uPlanetRadius * vScale;
    float Rs = uShellRadius * vScale;
    vec3 dir = normalize(vWorldPos - cameraPosition);
    vec3 oc = vCenter - cameraPosition;
    float t = max(dot(oc, dir), 0.0);
    vec3 closest = cameraPosition + dir * t;
    float b = length(closest - vCenter);
    float h = clamp((b - R) / max(Rs - R, 1e-5), 0.0, 1.0);
    float density = pow(1.0 - h, uFalloff);
    // Rays that graze the disc are hidden by the planet's depth anyway.
    vec3 up = normalize(closest - vCenter);
    float sd = dot(up, normalize(uSunDir));
    float lit = smoothstep(-0.35, 0.55, sd);
    float fwd = pow(max(dot(dir, normalize(uSunDir)), 0.0), 10.0) * smoothstep(-0.5, 0.1, sd);
    vec3 col = uAtmoColor * uSunColor * density * (lit + fwd * 1.6) * uStrength;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Procedural ring system. Radial bands from layered 1D noise with a few
// seeded gaps, lit on both faces, brighter when backlit (forward
// scatter), and cut by the planet's shadow cylinder.
export const proceduralRingVertexShader = `
  varying vec3 vLocal;
  varying vec3 vWorldPos;
  void main() {
    vLocal = position;
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    // Camera-relative (see proceduralGasGiant.js): world space on the GPU
    // cracks the mesh far from the origin in realistic scale.
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const proceduralRingFragmentShader = `
  precision highp float;
  varying vec3 vLocal;
  varying vec3 vWorldPos;
  uniform float uInner;
  uniform float uOuter;
  uniform vec3 uColA;
  uniform vec3 uColB;
  uniform vec3 uSunDir;
  uniform vec3 uSunColor;
  uniform vec3 uPlanetCenter;
  uniform float uPlanetRadius;
  uniform float uOpacity;
  uniform float uSeed;
  uniform vec3 uGaps;

  ${OKLAB_MIX_GLSL}

  float h1(float n) { return fract(sin(n * 12.9898 + uSeed) * 43758.5453); }
  float n1(float x) {
    float i = floor(x);
    float f = fract(x);
    return mix(h1(i), h1(i + 1.0), f * f * (3.0 - 2.0 * f));
  }

  void main() {
    float r = length(vLocal.xy);
    float t = (r - uInner) / (uOuter - uInner);
    if (t < 0.0 || t > 1.0) discard;
    float bands = n1(t * 9.0) * 0.5 + n1(t * 37.0) * 0.3 + n1(t * 140.0) * 0.2;
    float dens = smoothstep(0.2, 0.75, bands);
    dens *= smoothstep(0.0, 0.06, t) * smoothstep(1.0, 0.9, t);
    // Seeded gaps (x, y = positions in 0..1, z = width).
    dens *= smoothstep(0.0, uGaps.z, abs(t - uGaps.x));
    dens *= smoothstep(0.0, uGaps.z * 0.5, abs(t - uGaps.y));
    vec3 col = oklchMix(uColA, uColB, smoothstep(0.25, 0.8, n1(t * 21.0 + 4.0)));

    vec3 L = normalize(uSunDir);
    vec3 V = normalize(cameraPosition - vWorldPos);
    float back = pow(max(dot(-V, L), 0.0), 6.0);
    float light = 0.55 + 0.45 * (1.0 - dens) + back * 1.2 * (1.0 - dens * 0.5);

    // Planet shadow: cylinder behind the planet along -L.
    vec3 rel = vWorldPos - uPlanetCenter;
    float along = dot(rel, -L);
    float perp = length(rel + L * along);
    float shadow = along > 0.0 ? smoothstep(uPlanetRadius * 0.97, uPlanetRadius * 1.03, perp) : 1.0;

    col *= uSunColor * light * mix(0.04, 1.0, shadow);
    float alpha = dens * uOpacity;
    if (alpha < 0.01) discard;
    gl_FragColor = vec4(col, alpha);
  }
`;
